import { Component, ElementRef, Inject, OnInit, ViewChild, HostListener, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { MetaTagDataService } from '../../services/meta-tag-data.service';
import { RouteSettingsDataService } from '../../services/route-settings-data.service';
import { UploadHttpService } from '@core/services/upload-http.service';
import Swal from 'sweetalert2';
import { MetaTagSettingsDuplicateComponent } from '../meta-tag-settings-duplicate/meta-tag-settings-duplicate.component';
import { AppPermissionService } from '@core/services/app-permission.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kt-meta-tag-settings',
  templateUrl: './meta-tag-settings.component.html',
  styleUrls: ['./meta-tag-settings.component.scss']
})
export class MetaTagSettingsComponent implements OnInit, OnDestroy {
  // maps a "source" field to the fields it can auto-fill as a fallback
  private static readonly META_TAG_FALLBACK_TARGETS: { [sourceField: string]: string[] } = {
    meta_title: ['open_graph_title', 'page_title'],
    meta_desc: ['open_graph_desc'],
  };

  @ViewChild("keyword") keyword: ElementRef;

  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef>;
  @HostListener('window:beforeunload', ['$event'])
  confirmLeavingPageBeforeSaving($event) {
    if (this.hasChangesToSave()) {
      $event.preventDefault();
      $event.returnValue = true;
      return $event;
    }
  }

  form: FormGroup;
  dropdown = {
    openGraphType: this.dropdownHttpService.openGraphType,
    deviceType: [
      { id: 1, name: 'Desktop', icon: 'fas fa-desktop' },
      { id: 2, name: 'Mobile', icon: 'fas fa-mobile-alt' }
    ]
  };
  selectedDevice = this.dropdown.deviceType[0];
  defaultLocaleTab = 0;
  messages$ = this.metaTagDataService.messages$;
  isImageUploading = false;
  routesLoading = false;
  routes = [];
  routesBackUp: any;
  routeName = '';
  selectedRoute: any;
  params: any;
  metatags: any;
  metatagsLoading = false;
  words: any;
  wordCount: any;
  metatagsSettings: any;
  metatagsSettingsInitial: any;
  metatagsSettingsDuplicated: any;
  processingRoutes = [];
  isSavingAll = false;
  isSyncing = false;

  // permissions
  canEditMetaTagSettings: boolean;
  canDuplicateMetaTagSettings: boolean;

  private subscriptions = new Subscription();
 
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { mode: string, seo: any, locales: any },
    public dialogRef: MatDialogRef<MetaTagSettingsComponent>,
    private dropdownHttpService: DropdownHttpService,
    private metaTagDataService: MetaTagDataService,
    private routeSettingsDataService: RouteSettingsDataService,
    private uploadService: UploadHttpService,
    public dialog: MatDialog,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    // init form
    this.formInit();

    // get all meta settings from api
    this.getAllMetaSettings();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canEditMetaTagSettings = appPermissions.edit_meta_tag_settings;
      this.canDuplicateMetaTagSettings = appPermissions.duplicate_meta_tag_settings;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  unloadNotification($event: any): void {
    // Display a confirmation message
    $event.returnValue = true;
  }

  async onCloseDialog() {
    if (this.hasChangesToSave()) {
      const resp = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to proceed without saving the changes?',
        icon: 'warning',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#dd3333',
        denyButtonColor: '#0abb87',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        denyButtonText: 'Save All',
        reverseButtons: true,
      });

      if (resp.isConfirmed || resp.isDenied) {
        if (resp.isDenied) {
          await this.onSave(3);
        }
        this.dialogRef.close(true);
      }
    } else {
      this.dialogRef.close(true);
    }
  }

  async onSelectDevice(device: any) {
    if (this.isSavingAll || this.processingRoutes.length > 0) {
      return;
    }

    if (this.hasChangesToSave()) {
      const resp = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to proceed without saving the changes?',
        icon: 'warning',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#dd3333',
        denyButtonColor: '#0abb87',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        denyButtonText: 'Save All',
        reverseButtons: true,
      });

      if (resp.isConfirmed || resp.isDenied) {
        if (resp.isDenied) {
          await this.onSave(3);
        }

        this.selectedDevice = device;
        this.form.patchValue({
          device_type: this.selectedDevice.id
        });
        this.getAllMetaSettings();
      }
    } else {
      this.selectedDevice = device;
      this.form.patchValue({
        device_type: this.selectedDevice.id
      });
      this.getAllMetaSettings();
    }
  }

  onSearch() {
    this.routes = [];
    if (this.routeName !== undefined && this.routeName !== '' && this.routeName !== null) {
      this.routes = this.routesBackUp.filter(x => x.name.toLowerCase().includes(this.routeName.toLowerCase()));
    } else {
      this.routes = this.routesBackUp;
      // this.getRoutes();
    }
  }

  onSelectRoute(item: any) {
    if (this.processingRoutes.length > 0 || this.isSavingAll) {
      return;
    }
    this.selectedRoute = item;

    this.form.patchValue({
      seo_setting_route_id: item.id
    });

    this.data.locales.map(locale => {
      const metatagDetails = {
        setting_locale_id: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].id : locale.id,
        meta_title: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].meta_title : null,
        meta_desc: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].meta_desc : null,
        meta_keyword: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].meta_keyword : null,
        meta_robot: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].meta_robot : null,
        open_graph_title: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_title : null,
        open_graph_desc: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_desc : null,
        open_graph_url: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_url : null,
        open_graph_type: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_type : 1,
        open_graph_img: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_img : null,
        img_preview: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].open_graph_img : '',
        page_title: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].page_title : null,
        url_canonical: this.metatagsSettings['route_data'][item.id] && this.metatagsSettings['route_data'][item.id][locale.code] ? this.metatagsSettings['route_data'][item.id][locale.code].url_canonical : null,
      }

      this.form.patchValue({ details: { [locale.id]: metatagDetails } });
    });

    this.routesLoading = false;
    this.metatagsLoading = false;
  }

  onCountWords() {
    this.wordCount = this.keyword ? this.keyword.nativeElement.value.split(/\s+/) : 0;
    this.words = this.wordCount ? this.wordCount.length : 0;
    if (this.wordCount.length == 1 && this.wordCount[0] == '') {
      this.words = 0;
    }
  }

  onUploadFile(event: any, locale: any, index: number) {
    if (this.isSavingAll || this.processingRoutes.length > 0) {
      return;
    }

    this.isImageUploading = true;
    const file: File = event.target.files[0];
    const formData = new FormData();
    formData.append("files", file, file.name);
    formData.append("type", "uploads");
    this.uploadService.upload(formData).subscribe((res: any) => {
      const detailsForm = this.form.get('details') as FormGroup;
      const childForm = detailsForm.get(locale.id.toString()) as FormGroup;
      const img_path = res[0];
      if (childForm) {
        childForm.patchValue({ img_preview: img_path, open_graph_img: img_path });
      }
      this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale.code]['open_graph_img'] = img_path;
      this.cleanUpMetatagsSettings();
      this.isImageUploading = false;
      this.clearFileInput(index);
    });
    this.form.markAllAsTouched();
  }

  removeImage(locale: any, index) {
    const detailsForm = this.form.get('details') as FormGroup;
    const childForm = detailsForm.get(locale.id.toString()) as FormGroup;
    if (childForm) {
      childForm.patchValue({ img_preview: '', open_graph_img: null });
    }
    this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale.code]['open_graph_img'] = null;
    this.clearFileInput(index);
  }

  // clear dynamic input by index
  clearFileInput(index: number) {
    const input = this.fileInputs.toArray()[index];
    if (input) {
      input.nativeElement.value = '';
    }
  }

  async onSave(flag: any, route_id?: any) {
    // prevent double firing the save event
    if (route_id !== undefined) {
      if (this.processingRoutes.includes(route_id)) {
        return;
      }
    } else {
      if (this.isSavingAll) {
        return;
      }
      this.isSavingAll = true;
    }

    this.toggleFormEdit('disable');

    // prepare params
    let data = {
      ...this.metatagsSettings
    };
    if (route_id !== undefined) {
      data.seo_setting_route_id = route_id;
      this.processingRoutes.push(route_id);
    }
    Object.keys(data).forEach((key) => (data[key] == null || data[key] === '') && delete data[key]);

    // http request
    try {
      const res = await this.metaTagDataService.add(data).toPromise();

      if( route_id !== undefined ) {
        if( this.metatagsSettings['route_data'][route_id] === undefined) {
          if( this.metatagsSettingsInitial['route_data'][route_id] !== undefined ) {
            delete this.metatagsSettingsInitial['route_data'][route_id];
          }
        } else {
          if( this.metatagsSettingsInitial['route_data'][route_id] === undefined ) {
            this.metatagsSettingsInitial['route_data'][route_id] = {};
          }else{
            this.metatagsSettingsInitial['route_data'][route_id] = JSON.parse(JSON.stringify(this.metatagsSettings['route_data'][route_id]));
          }
          this.metatagsSettingsInitial['route_data'][route_id] = JSON.parse(JSON.stringify(this.metatagsSettings['route_data'][route_id]));
        }
        this.processingRoutes.splice(this.processingRoutes.indexOf(route_id), 1);
      } else {
        this.metatagsSettingsInitial = JSON.parse(JSON.stringify(this.metatagsSettings));
        this.isSavingAll = false;
      }

      // prompt alert
      if ([1, 3].includes(flag)) {
        this.messages$.next(res.message);
      }

      this.toggleFormEdit('enable');
    } catch (error) {
      if (route_id !== undefined) {
        this.processingRoutes.splice(this.processingRoutes.indexOf(route_id), 1);
      } else {
        this.isSavingAll = false;
      }
      this.toggleFormEdit('enable');
    }
  }

  toggleFormEdit(action: string) {
    const setControlState = (control: AbstractControl, action: string) => {
      if (control) {
        action === 'enable' ? control.enable() : control.disable();
      }
    }

    const detailsForm = this.form.get('details') as FormGroup;
    Object.keys(detailsForm.controls).forEach((localeId) => {
      const controlsToToggle = [
        'meta_title',
        'meta_desc',
        'meta_keyword',
        'meta_robot',
        'open_graph_title',
        'open_graph_desc',
        'open_graph_url',
        'open_graph_type'
      ];

      controlsToToggle.forEach((controlName) => {
        const control = detailsForm.get([localeId, controlName]);
        setControlState(control, action);
      });
    });
  }

  private getRoutes() {
    this.routes = [];
    this.routeSettingsDataService.getWithQuery(`?device_type=${this.form.value.device_type}&paginate=0`).subscribe((res: any) => {
      res.sort((a: any, b: any) => a.name.localeCompare(b.name));
      this.routesBackUp = res;
      this.onSearch();
      this.routes.length > 0 ? this.onSelectRoute(this.routes[0]) : '';
      if (this.routes.length <= 0) {
        this.routesLoading = false;
        this.metatagsLoading = false;
      }
    });
  }

  async getAllMetaSettings() {
    this.metatagsLoading = true;
    this.routesLoading = true;

    const field = {
      seo_setting_id: this.data.seo.id,
      device_type: this.form.value.device_type,
      paginate: false,
    }
    const data = this.filterFormFields(field);
    const params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
    const parameters = params ? `${params}` : '';

    // retrieve all meta settings from service
    await this.metaTagDataService.getWithQuery(`?${parameters}`).toPromise().then(res => {

      if (res) {
        this.metatagsSettings = {
          seo_setting_id: this.data.seo.id,
          device_type: this.form.value.device_type,
          route_data: {}
        };

        res.forEach((item: any) => {
          if (!this.metatagsSettings['route_data'][item.seo_setting_route_id]) {
            this.metatagsSettings['route_data'][item.seo_setting_route_id] = {};
          }

          if (!this.metatagsSettings['route_data'][item.seo_setting_route_id][item.locale]) {
            this.metatagsSettings['route_data'][item.seo_setting_route_id][item.locale] = {};
          }

          this.metatagsSettings['route_data'][item.seo_setting_route_id][item.locale] = {
            setting_locale_id: item.setting_locale_id,
            meta_title: item.meta_title,
            meta_desc: item.meta_desc,
            meta_keyword: item.meta_keyword,
            meta_robot: item.meta_robot,
            open_graph_title: item.open_graph_title,
            open_graph_desc: item.open_graph_desc,
            open_graph_url: item.open_graph_url,
            open_graph_type: item.open_graph_type,
            open_graph_img: item.open_graph_img,
            page_title: item.page_title,
            url_canonical: item.url_canonical,
          };
        });

      }
    });
    this.metatagsSettingsInitial = JSON.parse(JSON.stringify(this.metatagsSettings));

    // get all routes
    this.getRoutes();
  }

  private resetForm() {
    this.data.locales.map(res => {
      const metatag = this.metatags.find(metatag => metatag.setting_locale_id === res.id);
      const metatagDetails = {
        setting_locale_id: metatag ? metatag.setting_locale_id : res.id,
        meta_title: null,
        meta_desc: null,
        meta_keyword: null,
        meta_robot: null,
        open_graph_title: null,
        open_graph_desc: null,
        open_graph_url: null,
        open_graph_type: 1,
        open_graph_img: null,
        img_preview: '',
      }
      this.form.patchValue({ details: { [res.id]: metatagDetails } });
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) ? fields[key] = formData[key] : key);
    return fields;
  }

  private formInit() {
    const buildDetails = () => {
      let details = {};
      this.data.locales.map((element, index) => {
        const detailsGroup = new FormGroup({
          setting_locale_id: new FormControl(element.id, [Validators.required]),
          meta_title: new FormControl(null),
          meta_desc: new FormControl(null),
          meta_keyword: new FormControl(null),
          meta_robot: new FormControl(null),
          open_graph_title: new FormControl(null),
          open_graph_desc: new FormControl(null),
          open_graph_url: new FormControl(null),
          open_graph_type: new FormControl(1, [Validators.required]),
          open_graph_img: new FormControl(null),
          img_preview: new FormControl(''),
          page_title: new FormControl(null),
          url_canonical: new FormControl(null),
        });
        details = { ...details, [element.id]: detailsGroup };
      });
      return details;
    }
    this.form = new FormGroup({
      seo_setting_id: new FormControl(this.data.seo.id, [Validators.required]),
      seo_setting_route_id: new FormControl(null, [Validators.required]),
      device_type: new FormControl(this.selectedDevice.id, [Validators.required]),
      details: new FormGroup(buildDetails())
    });
  }

  onInputChange(locale_code: string, name: string, value: string) {
    if (this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id] === undefined) {
      this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id] = {};
    }
    this.data.locales.map((element, index) => {
      if( this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][element.code] === undefined ) {
        this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][element.code] = {
          setting_locale_id: element.id,
          meta_title: null,
          meta_desc: null,
          meta_keyword: null,
          meta_robot: null,
          open_graph_title: null,
          open_graph_desc: null,
          open_graph_url: null,
          open_graph_type: 1,
          open_graph_img: null,
          img_preview: '',
          page_title: null,
          url_canonical: null,
        };
      }
    });
    const newValue = (value == '' ? (name == 'img_preview' ? '' : null) : value);
    this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale_code][name] = newValue;

    const locale = this.data.locales.find(element => element.code === locale_code);
    if (locale) {
      this.applyAutoFillFallback(locale, name, newValue);
    }

    this.cleanUpMetatagsSettings();
  }

  private applyAutoFillFallback(locale: any, sourceField: string, value: string) {
    const targetFields = MetaTagSettingsComponent.META_TAG_FALLBACK_TARGETS[sourceField];
    if (!targetFields) {
      return;
    }

    const routeId = this.form.value.seo_setting_route_id;
    const detailsForm = this.form.get('details') as FormGroup;
    const childForm = detailsForm.get(locale.id.toString()) as FormGroup;

    targetFields.forEach(targetField => {
      this.metatagsSettings['route_data'][routeId][locale.code][targetField] = value;

      if (childForm) {
        childForm.patchValue({ [targetField]: value });
      }
    });
  }

  cleanUpMetatagsSettings() {
    if (this.metatagsSettingsInitial['route_data'][this.form.value.seo_setting_route_id] === undefined) {
      // is new route, check if is default values
      let is_default = true;
      Object.keys(this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id]).forEach((locale_code) => {
        Object.keys(this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale_code]).forEach((name) => {
          switch (name) {
            case 'meta_title':
            case 'meta_desc':
            case 'meta_keyword':
            case 'meta_robot':
            case 'open_graph_title':
            case 'open_graph_desc':
            case 'open_graph_url':
            case 'open_graph_img':
            case 'page_title':
            case 'url_canonical':
              if (this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale_code][name] !== null) {
                is_default = false;
              }
              break;
            case 'open_graph_type':
              if (this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale_code][name] !== 1) {
                is_default = false;
              }
              break;
            case 'img_preview':
              if (this.metatagsSettings['route_data'][this.form.value.seo_setting_route_id][locale_code][name] !== '') {
                is_default = false;
              }
              break;
          }
        });
      });

      if (is_default) {
        delete this.metatagsSettings.route_data[this.form.value.seo_setting_route_id];
      }
    }
  }

  shouldShowButton(route_id: any) {
    if (this.metatagsSettingsInitial.route_data[route_id] === undefined) {
      if (this.metatagsSettings.route_data[route_id] !== undefined) {
        return true;
      } else {
        return false;
      }
    } else {
      if (this.metatagsSettings.route_data[route_id] === undefined) {
        return true;
      } else {
        if (this.deepEqual(this.metatagsSettingsInitial.route_data[route_id], this.metatagsSettings.route_data[route_id])) {
          return false;
        } else {
          return true;
        }
      }
    }
  }

  hasChangesToSave() {
    if (this.deepEqual(this.metatagsSettings, this.metatagsSettingsInitial)) {
      return false;
    } else {
      return true;
    }
  }

  deepEqual(objA, objB) {
    if (objA === objB) {
      return true;
    }

    if (typeof objA !== 'object' || typeof objB !== 'object') {
      return false;
    }

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (!keysB.includes(key) || !this.deepEqual(objA[key], objB[key])) {
        return false;
      }
    }

    return true;
  }

  async onOpenDuplicateDialog() {
    if (this.isSavingAll || this.processingRoutes.length > 0) {
      return;
    }

    let dialogAttr = {
      width: '800px',
      data: {
        seo_setting_id: this.data.seo.id,
        device_type: this.selectedDevice.id,
        routes: this.routes,
      }
    };

    if (this.hasChangesToSave()) {
      const resp = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to proceed without saving the changes?',
        icon: 'warning',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#dd3333',
        denyButtonColor: '#0abb87',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        denyButtonText: 'Save All',
        reverseButtons: true,
      });

      if (resp.isConfirmed || resp.isDenied) {
        if (resp.isDenied) {
          await this.onSave(3);
        }
        const childDialogRef = this.dialog.open(MetaTagSettingsDuplicateComponent, dialogAttr);
        childDialogRef.afterClosed().subscribe((res: any) => {
          if (res !== undefined) {
            this.duplicateSetting(res);
          }
        });
      }
    } else {
      const childDialogRef = this.dialog.open(MetaTagSettingsDuplicateComponent, dialogAttr);
      childDialogRef.afterClosed().subscribe((res: any) => {
        if (res !== undefined) {
          this.duplicateSetting(res);
        }
      });
    }
  }

  async onSyncToMobile() {
    if (this.isSavingAll || this.processingRoutes.length > 0 || this.isSyncing) {
      return;
    }

    // Show sync confirmation
    const syncResp = await Swal.fire({
      title: 'Sync to Mobile',
      text: 'This will overwrite all Mobile meta tag settings with Desktop content. Are you sure you want to proceed?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#dd3333',
      confirmButtonText: 'Yes, Sync to Mobile',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!syncResp.isConfirmed) {
      return;
    }

    this.isSyncing = true;

    try {
      if (this.hasChangesToSave()) {
        await this.onSave(0);
      }

      // Fetch Desktop meta tag settings
      const field = {
        seo_setting_id: this.data.seo.id,
        device_type: 1,
        paginate: false,
      };
      const data: any = this.filterFormFields(field);
      const params = Object.keys(data).map(key => key + '=' + data[key]).join('&');

      const desktopData = await this.metaTagDataService.getWithQuery(`?${params}`).toPromise();

      // Build Mobile payload from Desktop data
      const mobileRouteData: any = {};
      if (desktopData) {
        desktopData.forEach((item: any) => {
          if (!mobileRouteData[item.seo_setting_route_id]) {
            mobileRouteData[item.seo_setting_route_id] = {};
          }
          mobileRouteData[item.seo_setting_route_id][item.locale] = {
            setting_locale_id: item.setting_locale_id,
            meta_title: item.meta_title,
            meta_desc: item.meta_desc,
            meta_keyword: item.meta_keyword,
            meta_robot: item.meta_robot,
            open_graph_title: item.open_graph_title,
            open_graph_desc: item.open_graph_desc,
            open_graph_url: item.open_graph_url,
            open_graph_type: item.open_graph_type,
            open_graph_img: item.open_graph_img,
            page_title: item.page_title,
            url_canonical: item.url_canonical,
          };
        });
      }

      const mobilePayload = {
        seo_setting_id: this.data.seo.id,
        device_type: 2,
        route_data: mobileRouteData,
      };

      // Auto save - send to API
      const res = await this.metaTagDataService.add(mobilePayload).toPromise();

      // Show success message
      this.messages$.next(res.message);

      // If currently viewing Mobile, refresh data to reflect synced content
      if (this.selectedDevice.id === 2) {
        this.getAllMetaSettings();
      }
    } catch (error) {
      // Error is handled by HTTP interceptor
    } finally {
      this.isSyncing = false;
    }
  }

  async duplicateSetting(res: any) {
    this.metatagsLoading = true;
    this.routesLoading = true;

    const field = {
      seo_setting_id: res.target_seo_setting_id,
      device_type: res.device_type,
      paginate: false,
    }
    const data = this.filterFormFields(field);
    const params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
    const parameters = params ? `${params}` : '';

    let newRouteData = {};
    await this.metaTagDataService.getWithQuery(`?${parameters}`).toPromise().then(res => {
      if (res) {
        res.forEach((item: any) => {
          if (!newRouteData[item.seo_setting_route_id]) {
            newRouteData[item.seo_setting_route_id] = {};
          }

          if (!newRouteData[item.seo_setting_route_id][item.locale]) {
            newRouteData[item.seo_setting_route_id][item.locale] = {};
          }

          newRouteData[item.seo_setting_route_id][item.locale] = {
            setting_locale_id: item.setting_locale_id,
            meta_title: item.meta_title,
            meta_desc: item.meta_desc,
            meta_keyword: item.meta_keyword,
            meta_robot: item.meta_robot,
            open_graph_title: item.open_graph_title,
            open_graph_desc: item.open_graph_desc,
            open_graph_url: item.open_graph_url,
            open_graph_type: item.open_graph_type,
            open_graph_img: item.open_graph_img,
            page_title: item.page_title,
            url_canonical: item.url_canonical,
          };
        });
      }
    });

    for (let i = 0; i < res.routes.length; i++) {
      if (newRouteData[res.routes[i]] === undefined) {
        if (this.metatagsSettings['route_data'][res.routes[i]] !== undefined) {
          delete this.metatagsSettings['route_data'][res.routes[i]];
        }
      } else {
        this.metatagsSettings['route_data'][res.routes[i]] = newRouteData[res.routes[i]];
      }
    }

    this.onSelectRoute(this.routes[0]);
  }

}
